import { Component, ContextType } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { DistributorContext } from '@mews-distributor/common';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews/text-keys';
import { Body, Button, Card, Icon, IconName, Stack, StackItem } from '@optimus-web/core';
import {
    API_MAXIMUM_INTERVAL_TIME_UNITS,
    ERROR_FATAL,
    ERROR_SESSION_EXPIRED,
    MEWS_SUPPORT_EMAIL,
} from '../../constants';
import { forceReload as forceReloadAction } from '../../modules/app/actions';
import {
    getSelectedEnterpriseEmail,
    getSelectedEnterpriseTelephone,
} from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { clearError as clearErrorAction } from '../../services/error/actions';
import { getErrorKey, getErrorMessage } from '../../services/error/selectors';
import { ApplicationState } from '../../types';

interface BaseErrorHandlerProps {
    enterpriseEmail: string | null;
    enterpriseTelephone: string | null;
    message: string | undefined;
    messageKey: Mews.TextKey | undefined;
    clearError: () => void;
    forceReload: () => void;
    onDismiss?: () => void;
    onTryAgain?: () => Promise<void> | void;
    supportEmail?: string;
}

class BaseErrorHandler extends Component<BaseErrorHandlerProps> {
    static contextType = DistributorContext;
    declare context: ContextType<typeof DistributorContext>;

    handleClickDismissButton = () => {
        const { clearError, onDismiss } = this.props;
        clearError();
        onDismiss?.();
    };

    handleClickTryAgainButton = () => {
        const { clearError, onTryAgain } = this.props;
        clearError();
        void onTryAgain?.();
    };

    handleClickRefreshButton = () => {
        const { forceReload } = this.props;
        forceReload();
    };

    componentDidCatch() {
        const { message, messageKey } = this.props;
        const { loggerService } = this.context;

        loggerService.log('error-boundary-error', 'ErrorBoundary component did catch', {
            message,
            messageKey,
        });
    }

    render() {
        const {
            onDismiss,
            onTryAgain,
            message,
            messageKey = ERROR_FATAL,
            supportEmail = MEWS_SUPPORT_EMAIL,
            enterpriseEmail,
            enterpriseTelephone,
        } = this.props;

        const showForceReloadButton = messageKey === ERROR_SESSION_EXPIRED;
        const showDismissButton = !!onDismiss && !showForceReloadButton;
        const showTryAgainButton = !!onTryAgain && !showForceReloadButton;
        const displayEnterpriseErrorMessage = enterpriseEmail != null || enterpriseTelephone != null;
        const errorMessage = displayEnterpriseErrorMessage ? (
            <LocalizedText textKey={TextKey.DistributorEnterpriseFatalErrorMessage} />
        ) : (
            <LocalizedText
                textKey={messageKey}
                substitutes={{ supportEmail, maxDateInterval: API_MAXIMUM_INTERVAL_TIME_UNITS }}
            />
        );
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        const finalMessage = message || errorMessage;
        return (
            <Stack vertical itemAlignment="center" spacing="200">
                <StackItem width="520" padding="300">
                    <Card padding="200">
                        <Stack spacing="200" vertical itemAlignment="center">
                            <Icon value={IconName.Problematic} size="large" />
                            <Stack spacing="200" vertical itemAlignment="center">
                                <Body>{finalMessage}</Body>
                                <StackItem>
                                    {enterpriseEmail == null ? null : <Body>{enterpriseEmail}</Body>}
                                    {enterpriseTelephone == null ? null : <Body>{enterpriseTelephone}</Body>}
                                </StackItem>
                            </Stack>
                            <Stack spacing="400">
                                {showForceReloadButton && (
                                    <Button variant="ghost" onClick={this.handleClickRefreshButton} type="submit">
                                        <LocalizedText textKey={TextKey.Refresh} />
                                    </Button>
                                )}

                                {showTryAgainButton && (
                                    <Button onClick={this.handleClickTryAgainButton} type="submit">
                                        <LocalizedText textKey={TextKey.TryAgain} />
                                    </Button>
                                )}
                                {showDismissButton && (
                                    <Button variant="primary" onClick={this.handleClickDismissButton} type="submit">
                                        <LocalizedText textKey={TextKey.Dismiss} />
                                    </Button>
                                )}
                            </Stack>
                        </Stack>
                    </Card>
                </StackItem>
            </Stack>
        );
    }
}
interface StateProps {
    enterpriseEmail: string | null;
    enterpriseTelephone: string | null;
    message: string | undefined;
    messageKey: Mews.TextKey | undefined;
}

export const ErrorHandler = connect(
    createStructuredSelector<ApplicationState, {}, StateProps>({
        enterpriseEmail: getSelectedEnterpriseEmail,
        enterpriseTelephone: getSelectedEnterpriseTelephone,
        message: getErrorMessage,
        messageKey: getErrorKey,
    }),
    {
        clearError: clearErrorAction,
        forceReload: forceReloadAction,
    }
)(BaseErrorHandler);
